/**
 * Fias Leads — React Query hooks (Phase 9).
 *
 * Thin wrappers over the /v1/arches/fias-leads/* API client. Every hook
 * is Holder-scoped: holderId comes from the arche-level <HolderSwitcher>
 * and disables the query when null.
 *
 * Mirrors the useFias360 pattern. Companies, Contacts, Activities,
 * Tasks, Email Templates, Contact Relationships are Vault-substrate
 * lenses (the same rows Fias 360 reads, when both arches point at the
 * same Holder). Pipelines, Deals, Automations, Custom Fields, Saved
 * Views are Leads-private.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type FiasLeadsCompanyDto,
  type FiasLeadsContactDto,
  type FiasLeadsContactExtensionDto,
  type FiasLeadsActivityDto,
  type FiasLeadsTaskDto,
  type FiasLeadsTaskStatus,
  type FiasLeadsTaskRegardingType,
  type FiasLeadsEmailTemplateDto,
  type FiasLeadsEmailTemplateCategory,
  type FiasLeadsContactRelationshipDto,
  type FiasLeadsContactRelationshipType,
  type FiasLeadsPipelineDto,
  type FiasLeadsPipelineStageType,
  type FiasLeadsDealDto,
  type FiasLeadsDealStatus,
  type FiasLeadsDealSortBy,
  type FiasLeadsPipelineSummaryStageDto,
  type FiasLeadsAutomationRuleDto,
  type FiasLeadsCustomFieldDefinitionDto,
  type FiasLeadsCustomFieldEntityType,
  type FiasLeadsSavedViewDto,
  type FiasLeadsSavedViewEntityType,
  type FiasLeadsActivityKind,
  type FiasLeadsActivityRegardingType,
  type FiasLeadsCreateCompanyInput,
  type FiasLeadsUpdateCompanyInput,
  type FiasLeadsCreateContactInput,
  type FiasLeadsUpdateContactInput,
  type FiasLeadsCreatePipelineInput,
  type FiasLeadsCreateDealInput,
  type FiasLeadsUpdateDealInput,
  type FiasLeadsCreateTaskInput,
  type FiasLeadsUpdateTaskInput,
  type FiasLeadsCreateActivityInput,
  type FiasLeadsCreateEmailTemplateInput,
  type FiasLeadsCreateAutomationInput,
  type FiasLeadsCreateCustomFieldInput,
  type FiasLeadsCreateSavedViewInput,
  type FiasLeadsCreateRelationshipInput,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

// ============================================================================
// Companies
// ============================================================================

export function useFiasLeadsCompanies(
  holderId: string | null,
  options?: { includeArchived?: boolean; limit?: number },
) {
  return useQuery({
    queryKey: queryKeys.fiasLeads.companies(holderId ?? ''),
    queryFn: () => api.listFiasLeadsCompanies(holderId!, options),
    select: (data) => data.companies,
    enabled: !!holderId,
  });
}

export function useFiasLeadsCompany(holderId: string | null, companyId: string | null) {
  return useQuery({
    queryKey:
      holderId && companyId
        ? queryKeys.fiasLeads.company(holderId, companyId)
        : ['fiasLeads', 'company', 'noop'],
    queryFn: () => api.getFiasLeadsCompany(holderId!, companyId!),
    select: (data) => data.company,
    enabled: !!(holderId && companyId),
  });
}

export function useCreateFiasLeadsCompany(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FiasLeadsCreateCompanyInput) =>
      api.createFiasLeadsCompany(holderId!, input),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.companies(holderId) });
    },
  });
}

export function useUpdateFiasLeadsCompany(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { companyId: string; input: FiasLeadsUpdateCompanyInput }) =>
      api.updateFiasLeadsCompany(holderId!, args.companyId, args.input),
    onSuccess: (_data, args) => {
      if (!holderId) return;
      qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.companies(holderId) });
      qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.company(holderId, args.companyId) });
    },
  });
}

export function useArchiveFiasLeadsCompany(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) => api.archiveFiasLeadsCompany(holderId!, companyId),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.companies(holderId) });
    },
  });
}

export function useSetOwnerFiasLeadsCompany(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { companyId: string; ownerPrincipalId: string | null }) =>
      api.setOwnerFiasLeadsCompany(holderId!, args.companyId, args.ownerPrincipalId),
    onSuccess: (_data, args) => {
      if (!holderId) return;
      qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.companies(holderId) });
      qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.company(holderId, args.companyId) });
    },
  });
}

// ============================================================================
// Contacts (Vault business contacts + Leads-private extension)
// ============================================================================

export function useFiasLeadsContacts(
  holderId: string | null,
  options?: { companyId?: string; includeArchived?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.fiasLeads.contactsList(holderId ?? '', options ?? {}),
    queryFn: () => api.listFiasLeadsContacts(holderId!, options),
    select: (data) => data.contacts,
    enabled: !!holderId,
  });
}

/** Minimum query length before the global search fires (avoids 1-char noise). */
export const FIAS_LEADS_SEARCH_MIN_LENGTH = 2;

/**
 * Global cross-entity search. Returns matching contacts, companies, and
 * deals — each with its related records. Disabled until the trimmed query
 * reaches {@link FIAS_LEADS_SEARCH_MIN_LENGTH}. Keeps the previous result
 * while the next one loads so the dropdown doesn't flash empty mid-type.
 */
export function useFiasLeadsGlobalSearch(holderId: string | null, q: string) {
  const trimmed = (q ?? '').trim();
  const enabled = !!holderId && trimmed.length >= FIAS_LEADS_SEARCH_MIN_LENGTH;
  return useQuery({
    queryKey: queryKeys.fiasLeads.search(holderId ?? '', trimmed),
    queryFn: () => api.fiasLeadsGlobalSearch(holderId!, trimmed),
    enabled,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
}

export function useFiasLeadsContact(holderId: string | null, contactId: string | null) {
  return useQuery({
    queryKey:
      holderId && contactId
        ? queryKeys.fiasLeads.contact(holderId, contactId)
        : ['fiasLeads', 'contact', 'noop'],
    queryFn: () => api.getFiasLeadsContact(holderId!, contactId!),
    enabled: !!(holderId && contactId),
  });
}

export function useCreateFiasLeadsContact(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FiasLeadsCreateContactInput) =>
      api.createFiasLeadsContact(holderId!, input),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.contacts(holderId) });
    },
  });
}

export function useUpdateFiasLeadsContact(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { contactId: string; input: FiasLeadsUpdateContactInput }) =>
      api.updateFiasLeadsContact(holderId!, args.contactId, args.input),
    onSuccess: (_data, args) => {
      if (!holderId) return;
      qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.contacts(holderId) });
      qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.contact(holderId, args.contactId) });
    },
  });
}

export function useArchiveFiasLeadsContact(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contactId: string) => api.archiveFiasLeadsContact(holderId!, contactId),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.contacts(holderId) });
    },
  });
}

// ============================================================================
// Relationships
// ============================================================================

export function useFiasLeadsRelationships(
  holderId: string | null,
  contactId: string | null,
  direction: 'from' | 'to' = 'from',
) {
  return useQuery({
    queryKey:
      holderId && contactId
        ? queryKeys.fiasLeads.relationships(holderId, contactId)
        : ['fiasLeads', 'relationships', 'noop'],
    queryFn: () =>
      api.listFiasLeadsRelationships(
        holderId!,
        direction === 'from' ? { fromContactId: contactId! } : { toContactId: contactId! },
      ),
    select: (data) => data.relationships,
    enabled: !!(holderId && contactId),
  });
}

export function useCreateFiasLeadsRelationship(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FiasLeadsCreateRelationshipInput) =>
      api.createFiasLeadsRelationship(holderId!, input),
    onSuccess: (_data, input) => {
      if (!holderId) return;
      qc.invalidateQueries({
        queryKey: queryKeys.fiasLeads.relationships(holderId, input.fromContactId),
      });
      qc.invalidateQueries({
        queryKey: queryKeys.fiasLeads.relationships(holderId, input.toContactId),
      });
    },
  });
}

export function useDeleteFiasLeadsRelationship(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (relationshipId: string) =>
      api.deleteFiasLeadsRelationship(holderId!, relationshipId),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.forHolder(holderId) });
    },
  });
}

// ============================================================================
// Pipelines + Stages
// ============================================================================

export function useFiasLeadsPipelines(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.fiasLeads.pipelines(holderId ?? ''),
    queryFn: () => api.listFiasLeadsPipelines(holderId!),
    select: (data) => data.pipelines,
    enabled: !!holderId,
  });
}

export function useCreateFiasLeadsPipeline(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FiasLeadsCreatePipelineInput) =>
      api.createFiasLeadsPipeline(holderId!, input),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.pipelines(holderId) });
    },
  });
}

export function useUpdateFiasLeadsPipeline(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { pipelineId: string; input: { name?: string; isDefault?: boolean } }) =>
      api.updateFiasLeadsPipeline(holderId!, args.pipelineId, args.input),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.pipelines(holderId) });
    },
  });
}

export function useDeleteFiasLeadsPipeline(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pipelineId: string) => api.deleteFiasLeadsPipeline(holderId!, pipelineId),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.pipelines(holderId) });
    },
  });
}

export function useAddFiasLeadsStage(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      pipelineId: string;
      input: { name: string; stageType?: FiasLeadsPipelineStageType; color?: string };
    }) => api.addFiasLeadsStage(holderId!, args.pipelineId, args.input),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.pipelines(holderId) });
    },
  });
}

export function useUpdateFiasLeadsStage(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      pipelineId: string;
      stageId: string;
      input: { name?: string; stageType?: FiasLeadsPipelineStageType; color?: string };
    }) => api.updateFiasLeadsStage(holderId!, args.pipelineId, args.stageId, args.input),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.pipelines(holderId) });
    },
  });
}

export function useDeleteFiasLeadsStage(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { pipelineId: string; stageId: string }) =>
      api.deleteFiasLeadsStage(holderId!, args.pipelineId, args.stageId),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.pipelines(holderId) });
    },
  });
}

export function useReorderFiasLeadsStages(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      pipelineId: string;
      stages: Array<{ stageId: string; displayOrder: number }>;
    }) => api.reorderFiasLeadsStages(holderId!, args.pipelineId, args.stages),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.pipelines(holderId) });
    },
  });
}

// ============================================================================
// Deals
// ============================================================================

export function useFiasLeadsDeals(
  holderId: string | null,
  options?: {
    pipelineId?: string;
    stageId?: string;
    contactId?: string;
    companyId?: string;
    ownerPrincipalId?: string;
    status?: FiasLeadsDealStatus;
    sortBy?: FiasLeadsDealSortBy;
    sortDir?: 'asc' | 'desc';
  },
) {
  return useQuery({
    queryKey: queryKeys.fiasLeads.dealsList(holderId ?? '', options ?? {}),
    queryFn: () => api.listFiasLeadsDeals(holderId!, options),
    select: (data) => data.deals,
    enabled: !!holderId,
  });
}

export function useFiasLeadsPipelineSummary(
  holderId: string | null,
  pipelineId: string | null,
  sort?: { sortBy?: FiasLeadsDealSortBy; sortDir?: 'asc' | 'desc' },
) {
  return useQuery({
    queryKey:
      holderId && pipelineId
        ? queryKeys.fiasLeads.pipelineSummary(holderId, pipelineId, sort)
        : ['fiasLeads', 'pipelineSummary', 'noop'],
    queryFn: () => api.getFiasLeadsPipelineSummary(holderId!, pipelineId!, sort),
    select: (data) => data.stages,
    enabled: !!(holderId && pipelineId),
  });
}

export function useFiasLeadsDeal(holderId: string | null, dealId: string | null) {
  return useQuery({
    queryKey:
      holderId && dealId
        ? queryKeys.fiasLeads.deal(holderId, dealId)
        : ['fiasLeads', 'deal', 'noop'],
    queryFn: () => api.getFiasLeadsDeal(holderId!, dealId!),
    select: (data) => data.deal,
    enabled: !!(holderId && dealId),
  });
}

export function useCreateFiasLeadsDeal(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FiasLeadsCreateDealInput) => api.createFiasLeadsDeal(holderId!, input),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.deals(holderId) });
    },
  });
}

export function useUpdateFiasLeadsDeal(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { dealId: string; input: FiasLeadsUpdateDealInput }) =>
      api.updateFiasLeadsDeal(holderId!, args.dealId, args.input),
    onSuccess: (_data, args) => {
      if (!holderId) return;
      qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.deals(holderId) });
      qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.deal(holderId, args.dealId) });
    },
  });
}

export function useMoveFiasLeadsDealStage(holderId: string | null) {
  const qc = useQueryClient();
  type SummaryData = { stages: FiasLeadsPipelineSummaryStageDto[] };
  return useMutation({
    mutationFn: (args: { dealId: string; stageId: string }) =>
      api.moveFiasLeadsDealStage(holderId!, args.dealId, args.stageId),
    // Optimistic update — without this the Kanban card flashes back to its
    // source column for the duration of the server roundtrip, then jumps
    // to its new column when the refetch lands.
    //
    // Ordering matters: setQueryData MUST run synchronously, before any
    // `await`. The dnd-kit drop animation in DealsKanbanPage measures
    // the active draggable's DOM rect at animation time to decide where
    // to glide the overlay. That measurement happens in the React
    // commit that fires when our `onDragEnd` handler returns — so the
    // cache update has to be in that same commit, batched alongside
    // `setActiveDeal(null)`. Putting `await qc.cancelQueries(...)`
    // first would push setQueryData past a microtask boundary, and the
    // animation would fire against the pre-move DOM (card still in the
    // source column → overlay glides backwards). cancelQueries' abort
    // signal is synchronous; the awaited promise is just for cleanup.
    onMutate: async (args) => {
      if (!holderId)
        return {
          previousSummaries: [] as Array<readonly [readonly unknown[], SummaryData | undefined]>,
        };
      // Snapshot every pipeline-summary cache for this Holder so we can
      // roll back on error. (The dragged deal lives in exactly one of
      // them but we don't know which without scanning.)
      const previousSummaries = qc.getQueriesData<SummaryData>({
        queryKey: [...queryKeys.fiasLeads.deals(holderId), 'pipelineSummary'],
      });
      for (const [key, data] of previousSummaries) {
        if (!data) continue;
        let dealRef: FiasLeadsDealDto | undefined;
        for (const stage of data.stages) {
          const found = stage.deals.find((d) => d.id === args.dealId);
          if (found) {
            dealRef = found;
            break;
          }
        }
        if (!dealRef) continue;
        const movedDeal: FiasLeadsDealDto = { ...dealRef, stage_id: args.stageId };
        const next: SummaryData = {
          stages: data.stages.map((stage) => {
            if (stage.stage_id === args.stageId) {
              // Prepend, not append: the pipeline-summary route orders
              // deals by `updated_at DESC` and the move endpoint bumps
              // `updated_at = now()`, so on refetch the moved deal lands
              // at index 0 of its new stage. Matching that here keeps
              // the dnd-kit drop overlay aimed at the right spot (top
              // of the column) and avoids a post-refetch reorder jump.
              // Filter is idempotent — guards against a stage that
              // already happens to hold this id.
              const without = stage.deals.filter((d) => d.id !== args.dealId);
              return { ...stage, deals: [movedDeal, ...without] };
            }
            if (stage.deals.some((d) => d.id === args.dealId)) {
              return { ...stage, deals: stage.deals.filter((d) => d.id !== args.dealId) };
            }
            return stage;
          }),
        };
        qc.setQueryData(key, next);
      }
      // Cancel in-flight summary refetches so they can't overwrite our
      // optimistic state. Synchronous abort; the await is just for
      // cleanup completion.
      await qc.cancelQueries({
        queryKey: [...queryKeys.fiasLeads.deals(holderId), 'pipelineSummary'],
      });
      return { previousSummaries };
    },
    onError: (_err, _args, ctx) => {
      if (!ctx) return;
      for (const [key, data] of ctx.previousSummaries) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      // Re-sync with the server (in case the server's view differs from
      // the optimistic one — e.g. computed fields like updated_at).
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.deals(holderId) });
    },
  });
}

export function useDeleteFiasLeadsDeal(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dealId: string) => api.deleteFiasLeadsDeal(holderId!, dealId),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.deals(holderId) });
    },
  });
}

// --- Deal contacts (many-to-many) ---

export function useFiasLeadsDealContacts(holderId: string | null, dealId: string | null) {
  return useQuery({
    queryKey:
      holderId && dealId
        ? queryKeys.fiasLeads.dealContacts(holderId, dealId)
        : ['fiasLeads', 'dealContacts', 'noop'],
    queryFn: () => api.listFiasLeadsDealContacts(holderId!, dealId!),
    select: (data) => data.contacts,
    enabled: !!(holderId && dealId),
  });
}

// Invalidate both the deal's contact list and the deal itself (its
// primary contact_id can change as a side effect of add/remove/set-primary).
function invalidateDealContacts(
  qc: ReturnType<typeof useQueryClient>,
  holderId: string | null,
  dealId: string,
) {
  if (!holderId) return;
  qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.dealContacts(holderId, dealId) });
  qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.deal(holderId, dealId) });
  qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.deals(holderId) });
}

export function useAddFiasLeadsDealContact(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { dealId: string; contactId: string; role?: string }) =>
      api.addFiasLeadsDealContact(holderId!, args.dealId, {
        contactId: args.contactId,
        role: args.role,
      }),
    onSuccess: (_data, args) => invalidateDealContacts(qc, holderId, args.dealId),
  });
}

export function useRemoveFiasLeadsDealContact(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { dealId: string; contactId: string }) =>
      api.removeFiasLeadsDealContact(holderId!, args.dealId, args.contactId),
    onSuccess: (_data, args) => invalidateDealContacts(qc, holderId, args.dealId),
  });
}

export function useSetFiasLeadsDealContactPrimary(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { dealId: string; contactId: string }) =>
      api.setFiasLeadsDealContactPrimary(holderId!, args.dealId, args.contactId),
    onSuccess: (_data, args) => invalidateDealContacts(qc, holderId, args.dealId),
  });
}

// ============================================================================
// Tasks
// ============================================================================

export function useFiasLeadsTasks(
  holderId: string | null,
  options?: {
    regardingType?: FiasLeadsTaskRegardingType;
    regardingId?: string;
    status?: FiasLeadsTaskStatus;
    assigneePrincipalId?: string;
    dueBefore?: string;
    includeDeleted?: boolean;
  },
) {
  return useQuery({
    queryKey: queryKeys.fiasLeads.tasksList(holderId ?? '', options ?? {}),
    queryFn: () => api.listFiasLeadsTasks(holderId!, options),
    select: (data) => data.tasks,
    enabled: !!holderId,
  });
}

export function useCreateFiasLeadsTask(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FiasLeadsCreateTaskInput) => api.createFiasLeadsTask(holderId!, input),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.tasks(holderId) });
    },
  });
}

export function useUpdateFiasLeadsTask(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { taskId: string; input: FiasLeadsUpdateTaskInput }) =>
      api.updateFiasLeadsTask(holderId!, args.taskId, args.input),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.tasks(holderId) });
    },
  });
}

export function useCompleteFiasLeadsTask(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => api.completeFiasLeadsTask(holderId!, taskId),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.tasks(holderId) });
    },
  });
}

export function useReopenFiasLeadsTask(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => api.reopenFiasLeadsTask(holderId!, taskId),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.tasks(holderId) });
    },
  });
}

export function useCancelFiasLeadsTask(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => api.cancelFiasLeadsTask(holderId!, taskId),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.tasks(holderId) });
    },
  });
}

export function useDeleteFiasLeadsTask(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => api.deleteFiasLeadsTask(holderId!, taskId),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.tasks(holderId) });
    },
  });
}

// ============================================================================
// Activities
// ============================================================================

export function useFiasLeadsActivities(
  holderId: string | null,
  options?: {
    regardingType?: FiasLeadsActivityRegardingType;
    regardingId?: string;
    kind?: FiasLeadsActivityKind;
    sortBy?: 'occurred_at' | 'created_at';
    sortDir?: 'asc' | 'desc';
    limit?: number;
  },
) {
  return useQuery({
    queryKey: queryKeys.fiasLeads.activitiesList(holderId ?? '', options ?? {}),
    queryFn: () => api.listFiasLeadsActivities(holderId!, options),
    select: (data) => data.activities,
    enabled: !!holderId,
  });
}

// Logging or deleting an activity changes a deal's derived `last_activity_at`
// and its position under the "Recently updated" sort (server orders deals by
// GREATEST(updated_at, last_activity)). Invalidate the deals list (and the
// Kanban pipeline-summary, which shares the `deals` key prefix) alongside the
// activities cache so those views re-sort instead of showing stale order.
function invalidateActivitiesAndDeals(
  qc: ReturnType<typeof useQueryClient>,
  holderId: string | null,
) {
  if (!holderId) return;
  qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.activities(holderId) });
  qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.deals(holderId) });
}

export function useCreateFiasLeadsActivity(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FiasLeadsCreateActivityInput) =>
      api.createFiasLeadsActivity(holderId!, input),
    onSuccess: () => invalidateActivitiesAndDeals(qc, holderId),
  });
}

export function useDeleteFiasLeadsActivity(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (activityId: string) => api.deleteFiasLeadsActivity(holderId!, activityId),
    onSuccess: () => invalidateActivitiesAndDeals(qc, holderId),
  });
}

// Merged timeline (activities + tasks) regarding a parent record.
export function useFiasLeadsTimeline(
  holderId: string | null,
  regardingType: FiasLeadsActivityRegardingType | null,
  regardingId: string | null,
  limit = 50,
) {
  return useQuery({
    queryKey:
      holderId && regardingType && regardingId
        ? queryKeys.fiasLeads.timeline(holderId, { regardingType, regardingId, limit })
        : ['fiasLeads', 'timeline', 'noop'],
    queryFn: () => api.getFiasLeadsTimeline(holderId!, regardingType!, regardingId!, limit),
    select: (data) => data.items,
    enabled: !!(holderId && regardingType && regardingId),
  });
}

// ============================================================================
// Email Templates
// ============================================================================

export function useFiasLeadsEmailTemplates(
  holderId: string | null,
  options?: { category?: FiasLeadsEmailTemplateCategory; includeArchived?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.fiasLeads.emailTemplates(holderId ?? ''),
    queryFn: () => api.listFiasLeadsEmailTemplates(holderId!, options),
    select: (data) => data.templates,
    enabled: !!holderId,
  });
}

export function useCreateFiasLeadsEmailTemplate(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FiasLeadsCreateEmailTemplateInput) =>
      api.createFiasLeadsEmailTemplate(holderId!, input),
    onSuccess: () => {
      if (holderId)
        qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.emailTemplates(holderId) });
    },
  });
}

export function useUpdateFiasLeadsEmailTemplate(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { templateId: string; input: Partial<FiasLeadsCreateEmailTemplateInput> }) =>
      api.updateFiasLeadsEmailTemplate(holderId!, args.templateId, args.input),
    onSuccess: () => {
      if (holderId)
        qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.emailTemplates(holderId) });
    },
  });
}

export function useArchiveFiasLeadsEmailTemplate(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => api.archiveFiasLeadsEmailTemplate(holderId!, templateId),
    onSuccess: () => {
      if (holderId)
        qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.emailTemplates(holderId) });
    },
  });
}

// ============================================================================
// Automations
// ============================================================================

export function useFiasLeadsAutomations(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.fiasLeads.automations(holderId ?? ''),
    queryFn: () => api.listFiasLeadsAutomations(holderId!),
    select: (data) => data.rules,
    enabled: !!holderId,
  });
}

export function useCreateFiasLeadsAutomation(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FiasLeadsCreateAutomationInput) =>
      api.createFiasLeadsAutomation(holderId!, input),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.automations(holderId) });
    },
  });
}

export function useToggleFiasLeadsAutomation(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) => api.toggleFiasLeadsAutomation(holderId!, ruleId),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.automations(holderId) });
    },
  });
}

export function useDeleteFiasLeadsAutomation(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) => api.deleteFiasLeadsAutomation(holderId!, ruleId),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.automations(holderId) });
    },
  });
}

export function useFiasLeadsAutomationExecutions(
  holderId: string | null,
  ruleId: string | null,
  limit = 100,
) {
  return useQuery({
    queryKey:
      holderId && ruleId
        ? queryKeys.fiasLeads.automationExecutions(holderId, ruleId)
        : ['fiasLeads', 'automation', 'executions', 'noop'],
    queryFn: () => api.getFiasLeadsAutomationExecutions(holderId!, ruleId!, limit),
    select: (data) => data.executions,
    enabled: !!(holderId && ruleId),
  });
}

// ============================================================================
// Custom Fields
// ============================================================================

export function useFiasLeadsCustomFields(
  holderId: string | null,
  entityType?: FiasLeadsCustomFieldEntityType,
) {
  return useQuery({
    queryKey: queryKeys.fiasLeads.customFields(holderId ?? '', entityType),
    queryFn: () => api.listFiasLeadsCustomFields(holderId!, entityType),
    select: (data) => data.fields,
    enabled: !!holderId,
  });
}

export function useCreateFiasLeadsCustomField(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FiasLeadsCreateCustomFieldInput) =>
      api.createFiasLeadsCustomField(holderId!, input),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.customFields(holderId) });
    },
  });
}

export function useDeleteFiasLeadsCustomField(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fieldId: string) => api.deleteFiasLeadsCustomField(holderId!, fieldId),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.customFields(holderId) });
    },
  });
}

// ============================================================================
// Saved Views
// ============================================================================

export function useFiasLeadsSavedViews(
  holderId: string | null,
  entityType?: FiasLeadsSavedViewEntityType,
) {
  return useQuery({
    queryKey: queryKeys.fiasLeads.savedViews(holderId ?? '', entityType),
    queryFn: () => api.listFiasLeadsSavedViews(holderId!, entityType),
    select: (data) => data.views,
    enabled: !!holderId,
  });
}

export function useCreateFiasLeadsSavedView(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FiasLeadsCreateSavedViewInput) =>
      api.createFiasLeadsSavedView(holderId!, input),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.savedViews(holderId) });
    },
  });
}

export function useDeleteFiasLeadsSavedView(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (viewId: string) => api.deleteFiasLeadsSavedView(holderId!, viewId),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.savedViews(holderId) });
    },
  });
}

// ============================================================================
// Dashboard / Reports / Deal Risk
// ============================================================================

export function useFiasLeadsDashboard(holderId: string | null, pipelineId?: string) {
  return useQuery({
    queryKey: queryKeys.fiasLeads.dashboard(holderId ?? '', pipelineId),
    queryFn: () => api.getFiasLeadsDashboard(holderId!, pipelineId),
    enabled: !!holderId,
  });
}

export function useFiasLeadsDealRisk(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.fiasLeads.dealRisk(holderId ?? ''),
    queryFn: () => api.getFiasLeadsDealRisk(holderId!),
    select: (data) => data.deals,
    enabled: !!holderId,
  });
}

export function useFiasLeadsPipelineVelocity(holderId: string | null, pipelineId?: string) {
  return useQuery({
    queryKey: queryKeys.fiasLeads.pipelineVelocity(holderId ?? '', pipelineId),
    queryFn: () => api.getFiasLeadsPipelineVelocity(holderId!, pipelineId),
    select: (data) => data.stages,
    enabled: !!holderId,
  });
}

export function useFiasLeadsForecast(holderId: string | null, pipelineId?: string) {
  return useQuery({
    queryKey: queryKeys.fiasLeads.forecast(holderId ?? '', pipelineId),
    queryFn: () => api.getFiasLeadsForecast(holderId!, pipelineId),
    select: (data) => data.forecast,
    enabled: !!holderId,
  });
}

export function useFiasLeadsActivityTrends(holderId: string | null, days = 30) {
  return useQuery({
    queryKey: queryKeys.fiasLeads.activityTrends(holderId ?? '', days),
    queryFn: () => api.getFiasLeadsActivityTrends(holderId!, days),
    select: (data) => data.trends,
    enabled: !!holderId,
  });
}

export function useFiasLeadsLostDeals(holderId: string | null, pipelineId?: string) {
  return useQuery({
    queryKey: queryKeys.fiasLeads.lostDeals(holderId ?? '', pipelineId),
    queryFn: () => api.getFiasLeadsLostDeals(holderId!, pipelineId),
    select: (data) => data.lostDeals,
    enabled: !!holderId,
  });
}

// ============================================================================
// Dedup
// ============================================================================

export function useFiasLeadsDuplicates(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.fiasLeads.dedupContacts(holderId ?? ''),
    queryFn: () => api.findFiasLeadsDuplicates(holderId!),
    select: (data) => data.duplicates,
    enabled: !!holderId,
  });
}

export function useMergeFiasLeadsContacts(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      canonicalId: string;
      mergedId: string;
      fields?: Record<string, unknown>;
    }) => api.mergeFiasLeadsContacts(holderId!, args.canonicalId, args.mergedId, args.fields),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.forHolder(holderId) });
    },
  });
}

// ============================================================================
// Cross-arche presence
// ============================================================================

export function useFiasLeadsCrossArchePresence(
  holderId: string | null,
  contactIds: string[],
  companyIds: string[] = [],
) {
  return useQuery({
    queryKey:
      holderId && (contactIds.length > 0 || companyIds.length > 0)
        ? queryKeys.fiasLeads.crossArchePresence(holderId, contactIds, companyIds)
        : ['fiasLeads', 'crossArchePresence', 'noop'],
    queryFn: () => api.getFiasLeadsCrossArchePresence(holderId!, { contactIds, companyIds }),
    enabled: !!holderId && (contactIds.length > 0 || companyIds.length > 0),
  });
}

// Type re-exports so views can pull domain types from this file
// instead of reaching into @/lib/api.
export type {
  FiasLeadsCompanyDto,
  FiasLeadsContactDto,
  FiasLeadsContactExtensionDto,
  FiasLeadsActivityDto,
  FiasLeadsTaskDto,
  FiasLeadsEmailTemplateDto,
  FiasLeadsContactRelationshipDto,
  FiasLeadsContactRelationshipType,
  FiasLeadsPipelineDto,
  FiasLeadsDealDto,
  FiasLeadsAutomationRuleDto,
  FiasLeadsCustomFieldDefinitionDto,
  FiasLeadsSavedViewDto,
};
